/**
 * OnboardingHeroComponent — bannière d'onboarding pour la page d'accueil.
 *
 * Affiche un encart riche avec :
 *   - Progress bar globale
 *   - Mini résumé "X étapes restantes"
 *   - 3 prochaines étapes prioritaires (chips cliquables)
 *   - Bouton primaire qui ouvre le drawer complet
 *   - Bouton secondaire "Masquer pour cette session"
 *
 * Caché automatiquement si l'onboarding est complet ou si l'utilisateur
 * a marqué la session "hidden". Léger : aucune logique d'état, tout
 * vient du BusinessOnboardingService.
 */

import { CommonModule } from '@angular/common';
import { Component, computed, inject } from '@angular/core';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';

import { VdButtonComponent } from '../../ui/button/button.component';
import {
  BusinessOnboardingService,
  BusinessOnboardingStep,
} from '../../services/business-onboarding.service';

@Component({
  selector: 'app-onboarding-hero',
  standalone: true,
  imports: [CommonModule, TranslocoModule, VdButtonComponent],
  templateUrl: './onboarding-hero.component.html',
  styleUrls: ['./onboarding-hero.component.css'],
})
export class OnboardingHeroComponent {
  private readonly svc = inject(BusinessOnboardingService);
  private readonly i18n = inject(TranslocoService);

  /** Traduit une clé i18n pour les attributs aria (template-friendly). */
  t(key: string, params?: Record<string, unknown>): string {
    return this.i18n.translate(key, params);
  }

  readonly state = this.svc.state;
  readonly progress = this.svc.progress;
  readonly remainingCount = this.svc.remainingCount;
  readonly isComplete = this.svc.isComplete;
  readonly visible = this.svc.visibleOnSurfaces;

  /** 3 premières étapes non-done — pour les chips prioritaires. */
  readonly priorityChips = computed<BusinessOnboardingStep[]>(() => {
    const s = this.state();
    if (!s) return [];
    return s.steps.filter((st) => st.status !== 'done').slice(0, 3);
  });

  openDrawer(): void {
    this.svc.openDrawer();
  }

  startStep(step: BusinessOnboardingStep, ev: Event): void {
    ev.stopPropagation();
    this.svc.startStep(step);
  }

  hide(): void {
    this.svc.markHidden();
  }
}
